import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Navigation, Clock, Repeat } from 'lucide-react';
import { xamplinApi, entityIdOf, type XpStructureEvent } from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';
import { HelpHint } from '@/features/help/HelpHint';

/**
 * Same-day Route & Proximity (build-v2 F5, Plan mode). Clusters a project's
 * events by calendar day; for any day with 2+ geolocated venues it computes
 * straight-line × road-factor distances, an in-order route (chronological), and
 * estimated drive legs — drawn as a schematic SVG. Distances are estimates.
 */
const ROAD_FACTOR = 1.3; // straight-line → road distance fudge
const CITY_MPH = 22;

interface Stop {
  id: number;
  name: string;
  lat: number;
  lng: number;
  time: number; // ms epoch for ordering
  group?: string;
  reporters: number;
}
interface Leg {
  mi: number;
  min: number;
}
interface DayCluster {
  date: string;
  stops: Stop[];
  legs: Leg[];
  totalMi: number;
  nudge?: string;
}

function toRad(d: number) {
  return (d * Math.PI) / 180;
}
function haversineMi(a: Stop, b: Stop) {
  const R = 3958.8;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}
const roadMi = (a: Stop, b: Stop) => haversineMi(a, b) * ROAD_FACTOR;
const driveMin = (mi: number) => Math.max(2, Math.round((mi / CITY_MPH) * 60));

function dayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

export function RoutePage() {
  const { id } = useParams<{ id: string }>();
  const projectId = id ? Number(id) : 0;

  const q = useQuery({
    queryKey: queryKeys.xamplin.projectStructure(projectId),
    queryFn: () => xamplinApi.projects.structure(projectId),
    enabled: projectId > 0,
  });

  const clusters = useMemo<DayCluster[]>(() => {
    const groups = q.data?.structured?.project?.eventGroups ?? [];
    const events: XpStructureEvent[] = groups.flatMap((g) =>
      (g.events ?? []).map((e) => ({ ...e, location: e.location ?? g.name })),
    );
    const stops: Stop[] = events
      .filter((e) => e.lat != null && e.lng != null && e.startDate)
      .map((e) => ({
        id: entityIdOf(e.id),
        name: e.name ?? '—',
        lat: Number(e.lat),
        lng: Number(e.lng),
        time: Date.parse(e.startDate as string),
        group: e.location ?? undefined,
        reporters: e.reporters?.length ?? 0,
      }))
      .filter((s) => Number.isFinite(s.time));

    const byDay = new Map<string, Stop[]>();
    for (const s of stops) {
      const k = dayKey(s.time);
      const list = byDay.get(k) ?? [];
      list.push(s);
      byDay.set(k, list);
    }

    const out: DayCluster[] = [];
    for (const [date, list] of byDay) {
      if (list.length < 2) continue; // a route needs 2+ stops
      const ordered = [...list].sort((a, b) => a.time - b.time);
      const legs: Leg[] = [];
      let totalMi = 0;
      for (let i = 1; i < ordered.length; i++) {
        const mi = roadMi(ordered[i - 1], ordered[i]);
        totalMi += mi;
        legs.push({ mi, min: driveMin(mi) });
      }
      // RouteNudge (G14): a staffed stop that finishes before a nearby (≤10mi)
      // understaffed stop starts → suggest the finishing team rolls over.
      let nudge: string | undefined;
      for (let i = 0; i < ordered.length && !nudge; i++) {
        const donor = ordered[i];
        if (donor.reporters === 0) continue;
        for (let j = i + 1; j < ordered.length; j++) {
          const gap = ordered[j];
          if (gap.reporters > 0) continue;
          if (roadMi(donor, gap) <= 10) {
            nudge = `«${donor.name}» termina cerca de «${gap.name}» (sin staff) — el equipo puede rodar (${roadMi(donor, gap).toFixed(1)} mi).`;
            break;
          }
        }
      }
      out.push({ date, stops: ordered, legs, totalMi, nudge });
    }
    // Most stops first — the busiest days lead.
    return out.sort((a, b) => b.stops.length - a.stops.length);
  }, [q.data]);

  return (
    <div className="page flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Link
          to={`/projects/${projectId}`}
          className="grid h-8 w-8 place-items-center rounded-lg border border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            Route & proximity
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            Same-day clusters · estimaciones
          </p>
        </div>
        <div className="ml-auto">
          <HelpHint section="route" />
        </div>
      </div>

      {q.isLoading && (
        <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando rutas…</div>
      )}
      {!q.isLoading && clusters.length === 0 && (
        <div className="mono rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-4 py-3 text-sm text-[var(--color-xk-text-muted)]">
          No hay días con 2+ venues geolocalizados en este proyecto.
        </div>
      )}

      <div className="flex flex-col gap-5">
        {clusters.map((c) => (
          <DayClusterCard key={c.date} cluster={c} />
        ))}
      </div>
    </div>
  );
}

function DayClusterCard({ cluster }: { cluster: DayCluster }) {
  const dateLabel = new Date(cluster.date + 'T00:00:00Z').toLocaleDateString('es', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return (
    <div className="rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-sm font-bold capitalize text-[var(--color-xk-lavender)]">
          {dateLabel}
        </h2>
        <span className="mono text-[11px] text-[var(--color-xk-text-muted)]">
          {cluster.stops.length} stops · ~{Math.round(cluster.totalMi)} mi ·{' '}
          {cluster.legs.reduce((s, l) => s + l.min, 0)} min drive
        </span>
      </div>

      {cluster.nudge && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-[#4dd28b]/40 bg-[#4dd28b]/10 px-3 py-2 text-[11px] text-[var(--color-xk-lavender)]">
          <Repeat className="mt-0.5 h-3.5 w-3.5 flex-none text-[#4dd28b]" />
          <span>{cluster.nudge}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <RouteMap stops={cluster.stops} />
        <ol className="flex flex-col gap-0">
          {cluster.stops.map((s, i) => (
            <li key={s.id}>
              <div className="flex items-center gap-2.5">
                <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-[var(--color-xk-accent)] text-[11px] font-bold text-white">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-[var(--color-xk-lavender)]">{s.name}</div>
                  <div className="mono flex items-center gap-1 text-[10px] text-[var(--color-xk-text-subtle)]">
                    <Clock className="h-3 w-3" />
                    {new Date(s.time).toLocaleTimeString('es', {
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: 'UTC',
                    })}
                  </div>
                </div>
              </div>
              {i < cluster.legs.length && (
                <div className="my-0.5 ml-3 flex items-center gap-1.5 border-l border-dashed border-[var(--color-xk-border)] pl-4 text-[10px] text-[var(--color-xk-text-muted)]">
                  <Navigation className="h-3 w-3 text-[var(--color-xk-accent)]" />
                  {cluster.legs[i].mi.toFixed(1)} mi · {cluster.legs[i].min} min
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/** Schematic SVG: venues placed by normalized lat/lng, connected in order. */
function RouteMap({ stops }: { stops: Stop[] }) {
  const W = 360;
  const H = 200;
  const pad = 24;
  const lats = stops.map((s) => s.lat);
  const lngs = stops.map((s) => s.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const spanLat = maxLat - minLat || 1;
  const spanLng = maxLng - minLng || 1;
  const x = (lng: number) => pad + ((lng - minLng) / spanLng) * (W - pad * 2);
  // SVG y grows downward; invert lat so north is up.
  const y = (lat: number) => pad + (1 - (lat - minLat) / spanLat) * (H - pad * 2);
  const pts = stops.map((s) => ({ px: x(s.lng), py: y(s.lat) }));
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.px.toFixed(1)} ${p.py.toFixed(1)}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="h-[200px] w-full rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)]"
    >
      <path d={path} fill="none" stroke="var(--color-xk-accent)" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7} />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.px} cy={p.py} r={9} fill="var(--color-xk-accent)" opacity={0.18} />
          <circle cx={p.px} cy={p.py} r={5} fill="var(--color-xk-accent)" />
          <text x={p.px} y={p.py + 3} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">
            {i + 1}
          </text>
        </g>
      ))}
      <text x={pad} y={H - 6} fontSize="8" fill="var(--color-xk-text-subtle)" className="mono">
        ◹ schematic · not to scale
      </text>
    </svg>
  );
}
